"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.shuffle = exports.sample = exports.sampleSizeWithOptionalSeed = exports.randomBoolArray = exports.randomInArray = exports.randomValueIndex = exports.randomInRange = exports.randomAround = exports.randomAroundPercent = exports.randomFromDistribution = void 0;
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const random_1 = __importDefault(require("random"));
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const MathUtils_1 = require("./MathUtils");
// TODO: Document those methods and try to type them better. They were just copy-pasted from original file
/**
 * Get a random value from a certain distribution
 * @param distribution array or Map with key = Number and value = probability ([0.0,1.0])
 * @param randomNumber
 * @param distributionSum
 * @param except
 * @returns
 */
const randomFromDistribution = function (distribution, randomNumber = random_1.default.float(0.0, 1.0), distributionSum = 1.0, except = []) {
    if (distributionSum === 0) {
        throw 'Distribution sum is invalid';
    }
    let cummulativeProbability = 0.0;
    const randomFromSum = randomNumber * distributionSum;
    if (Array.isArray(distribution)) {
        let prevKey = 0;
        // Array
        for (let i = 0, count = distribution.length; i < count; i++) {
            cummulativeProbability += distribution[i];
            if (randomFromSum < cummulativeProbability) {
                if (!except.includes(i)) {
                    return i;
                }
            }
            prevKey = i;
        }
        return prevKey;
    }
    if (typeof distribution.entries === 'function') {
        let prevKey = 0;
        // Map
        for (const [key, probability] of distribution.entries()) {
            cummulativeProbability += probability;
            if (randomFromSum < cummulativeProbability) {
                if (!except.includes(key)) {
                    return key;
                }
            }
            prevKey = key;
        }
        return prevKey;
    }
    // Object
    let prevKey = 0;
    for (const key in distribution) {
        cummulativeProbability += distribution[key];
        if (randomFromSum < cummulativeProbability) {
            if (!except.includes(key)) {
                return key;
            }
        }
        prevKey = key;
    }
    return prevKey;
};
exports.randomFromDistribution = randomFromDistribution;
/**
 *
 * @param value The value around which to get the random value
 * @param rangeMinusPercent
 * @param rangePlusPercent
 * @param randomGenerator
 * @returns
 */
const randomAroundPercent = function (value, rangeMinusPercent = 0.1, rangePlusPercent, randomGenerator = random_1.default) {
    const rangePlus = rangePlusPercent === undefined ? rangeMinusPercent : rangePlusPercent;
    return randomGenerator.float(value * (1 - rangeMinusPercent), value * (1 + rangePlus));
};
exports.randomAroundPercent = randomAroundPercent;
const randomAround = function (value, rangeMinus = 1, rangePlus, randomGenerator = random_1.default) {
    const plusRange = rangePlus === undefined ? rangeMinus : rangePlus;
    return randomGenerator.float(value - rangeMinus, value + plusRange);
};
exports.randomAround = randomAround;
const sampleSizeWithOptionalSeed = function (array, n /* sample size */, randomGenerator = random_1.default, seed) {
    // from lodash.sampleSize
    if (seed) {
        randomGenerator = random_1.default.clone(seed);
    }
    n = n === null ? 1 : n;
    const length = array === null ? 0 : array.length;
    if (!length || n < 1) {
        return [];
    }
    n = n > length ? length : n;
    let index = -1;
    const lastIndex = length - 1;
    const result = (0, cloneDeep_1.default)(array);
    while (++index < n) {
        const rand = index + Math.floor(randomGenerator.float() * (lastIndex - index + 1));
        const value = result[rand];
        result[rand] = result[index];
        result[index] = value;
    }
    return result.slice(0, n);
};
exports.sampleSizeWithOptionalSeed = sampleSizeWithOptionalSeed;
const shuffle = function (array, randomGenerator = random_1.default, seed) {
    return sampleSizeWithOptionalSeed(array, array.length, randomGenerator, seed);
};
exports.shuffle = shuffle;
const randomInArray = function (array, randomGenerator = random_1.default, seed) {
    const sample = sampleSizeWithOptionalSeed(array, 1, randomGenerator, seed);
    if (sample && sample.length === 1) {
        return sample[0];
    }
    else {
        throw 'randomInArray: undefined sample, or invalid length';
    }
};
exports.randomInArray = randomInArray;
// returns only integer
const randomInRange = function (range, randomGenerator = random_1.default, seed) {
    if (!range ||
        !Array.isArray(range) ||
        range.length !== 2 ||
        !Number.isInteger(range[0]) ||
        !Number.isInteger(range[1]) ||
        range[1] < range[0]) {
        throw 'randomInRange: Invalid range, must be positive integer';
    }
    const rangeDiff = range[1] - range[0];
    const values = (0, MathUtils_1.sequentialArray)(rangeDiff + 1, range[0], 1);
    return randomInArray(values, randomGenerator, seed);
};
exports.randomInRange = randomInRange;
const randomValueIndex = function (array, value, randomGenerator = random_1.default, seed) {
    if (!array || !Array.isArray(array) || array.length === 0) {
        return null;
    }
    const valueIndexes = [];
    for (let i = 0, count = array.length; i < count; i++) {
        if (array[i] === value) {
            valueIndexes.push(i);
        }
    }
    if (valueIndexes.length > 0) {
        return randomInArray(valueIndexes, randomGenerator, seed);
    }
    return null;
};
exports.randomValueIndex = randomValueIndex;
const randomBoolArray = function (n /* array size */, numberOfTrue, randomGenerator = random_1.default, seed) {
    if (!Number.isInteger(n) || n < 0) {
        throw 'randomBoolArray: Array size must be a positive integer';
    }
    const indexes = (0, MathUtils_1.sequentialArray)(n);
    const nbTrue = numberOfTrue === undefined ? randomGenerator.int(0, n) : numberOfTrue;
    const trueIndexes = sampleSizeWithOptionalSeed(indexes, nbTrue, randomGenerator, seed);
    const boolArray = new Array(n);
    boolArray.fill(false);
    for (let i = 0, count = trueIndexes.length; i < count; i++) {
        boolArray[trueIndexes[i]] = true;
    }
    return boolArray;
};
exports.randomBoolArray = randomBoolArray;
const sample = sampleSizeWithOptionalSeed; // alias
exports.sample = sample;
//# sourceMappingURL=RandomUtils.js.map